import {
  EventEmitter,
  dompurifier,
  getTranslatedText,
  useActivityTracker
} from '@nile/components/src/Utils/helpers';
import { AIOnboardingRatingComponent } from '@nile/components';
import {
  GET_SUMMARY_CONTENT,
  GET_SUMMARY_TYPES,
  MLClient
} from '@nile/graphql/src';
import { SVGIcon } from '@superops/super-icons';
import { Badge, Button, TextStyle } from '@superops/ui-design-system';
import { cloneDeep, isEmpty, isEqual } from 'lodash';
import React, { useContext, useEffect, useState, useRef } from 'react';
import { useQuery } from '@apollo/client';
import { FormattedMessage, useIntl } from 'react-intl';
import AIContainerContext from '../context/AIContainerContext';
import {
  AI_FEEDBACK_TYPES,
  AI_ERROR_CODE,
  GLOBAL_AI_BOT_STATE
} from '../helpers';
import AICopyAndPaste from './AICopyAndPaste';
import AIFeedback from './AIFeedback';
import GlobalAIBot from './GlobalAIBot';
import RandomText from './RandomText';
import { useAIStore } from '@nile/components/src/Components/AI/store/aiStore.tsx';

const DEFAULT_RATING_DELAY = 10000; // By default we'll show rating component after 10 seconds

const AIDataComponent = (props) => {
  const { setLoading, options, headerText } = props;
  const intl = useIntl();

  const aiContainerContext = useContext(AIContainerContext);

  const aiData = useAIStore((s) => s.aiData);
  const setAIData = useAIStore((s) => s.setAIData);

  const summaryData = useAIStore((s) => s.summaryData);
  const setSummaryData = useAIStore((s) => s.setSummaryData);

  const summaryTypes = useAIStore((s) => s.summaryTypes);
  const setSummaryTypes = useAIStore((s) => s.setSummaryTypes);

  const summaryType = useAIStore((s) => s.summaryType);
  const setSummaryType = useAIStore((s) => s.setSummaryType);

  const errors = useAIStore((s) => s.errors);
  const setErrors = useAIStore((s) => s.setErrors);

  const showRatingComponent = useAIStore((s) => s.showRatingComponent);
  const setShowRatingComponent = useAIStore((s) => s.setShowRatingComponent);

  const dataRef = useRef(null);
  dataRef.current = aiData;

  const setData = (updatedData) => {
    dataRef.current = updatedData;
  };

  const data = dataRef.current;

  const [apiLoading, setApiLoading] = useState(false);
  const [hideFeedback, setHideFeedback] = useState(false);

  const isBeta = data?.beta;

  const AI_TYPE = data?.type || 'SUMMARY';

  const handleAIError = (params) => {
    const commonError = getTranslatedText(intl, 'msp.ai.common.error.message');
    if (params?.errorCode) {
      const errorCode = params?.errorCode;
      const errorCodeString = errorCode.toString();
      const errorMessage = AI_ERROR_CODE?.[errorCodeString] || commonError;
      setErrors({
        errorCode: errorCode,
        message: getTranslatedText(intl, errorMessage)
      });
    } else {
      setErrors({
        errorCode: null,
        message: commonError
      });
    }
  };

  const {
    loading: summaryTypeLoading,
    error: summaryTypeError,
    data: summaryTypeData
  } = useQuery(GET_SUMMARY_TYPES, {
    client: MLClient,
    fetchPolicy: 'no-cache',
    skip: data?.type !== 'SUMMARY' || !isEmpty(summaryTypes),
    onCompleted(res) {
      const types = res?.getSummaryTypes;
      setSummaryTypes(types);
    }
  });

  const shouldSkipApi = (params) => {
    if (!isEmpty(summaryData)) {
      return isEqual(aiData, params);
    }
    return false;
  };

  const ratingTimeOutRef = useRef(null);
  const ratingDelay = data?.ratingDelay || DEFAULT_RATING_DELAY;

  const showRatingComponentOnMount = () => {
    if (ratingTimeOutRef.current) {
      clearTimeout(ratingTimeOutRef.current);
    }
    if (data?.showRatingComponentOnMount && !showRatingComponent) {
      ratingTimeOutRef.current = setTimeout(() => {
        setShowRatingComponent(true);
      }, ratingDelay);
    }
  };

  const getSummary = (params) => {
    const shouldSkip = shouldSkipApi(params);
    if (shouldSkip) {
      return;
    }
    setAIData(params);
    setApiLoading(true);
    setLoading(true);
    setErrors(null);
    const Api = params?.Api || params?.data?.Api || GET_SUMMARY_CONTENT;
    const variables = params?.variables || params?.data?.variables;
    const client = params?.client || params?.data?.client || MLClient; //'ML_CLIENT'
    const accessorKey =
      params?.accessorKey || params?.data?.accessorKey || 'getSummaryContent';
    const isMutation = params?.queryType === 'Mutation';
    if (params?.summaryType) {
      setSummaryType(params?.summaryType);
    }

    if (isMutation) {
      client
        .mutate({
          mutation: Api,
          variables: variables,
          fetchPolicy: 'no-cache'
        })
        .then((res) => {
          setApiLoading(false);
          setLoading(false);
          setHideFeedback(false);
          if (res) {
            const responseData = res?.data?.[accessorKey];
            if (responseData) {
              setSummaryData(responseData);
              showRatingComponentOnMount();
            } else {
              handleAIError();
            }
          }
        })
        .catch((err) => {
          setSummaryData(null);
          setApiLoading(false);
          setLoading(false);
        });
    } else {
      client
        .query({
          query: Api,
          variables: variables,
          fetchPolicy: 'no-cache'
        })
        .then((res) => {
          setApiLoading(false);
          setLoading(false);
          setHideFeedback(false);
          if (res) {
            if (res?.data?.[accessorKey]) {
              setSummaryData(res?.data?.[accessorKey]);
              showRatingComponentOnMount();
            } else {
              handleAIError();
            }
          }
        })
        .catch((err) => {
          if (err) {
            setSummaryData(null);
            setApiLoading(false);
            setLoading(false);
            // handleAIError();
          }
        });
    }
  };

  const onBack = () => {
    EventEmitter.dispatch('globalAIBackClicked');
    if (aiContainerContext?.onBack) {
      aiContainerContext?.onBack();
    }
  };
  const sendActivity = (activity) => {
    const type = activity?.type;
    const ticketId = data?.variables?.input?.itemId || data?.workItem?.itemId;
    const feedbackComment = activity?.feedbackComment;
    const commentData = feedbackComment ? { comments: feedbackComment } : {};
    if (data?.sendFeedback) {
      data?.sendFeedback({
        data: data,
        response: summaryData,
        feedbackComment: feedbackComment,
        type: type
      });
    } else if (AI_TYPE === 'SUMMARY') {
      useActivityTracker({
        event: 'Summarization - Feedback',
        data: {
          feedback: type,
          ticketId: ticketId,
          summary: summaryData,
          ...commentData
        }
      });
    }
  };

  const changeSummaryType = (type) => {
    let dataToUpdate = cloneDeep(data);

    const input = dataToUpdate.variables?.input;
    const variables = {
      input: {
        ...input,
        summaryType: type
      }
    };

    dataToUpdate = {
      ...dataToUpdate,
      variables: variables,
      summaryType: type
    };

    setData(dataToUpdate);
    getSummary(dataToUpdate);
  };

  useEffect(() => {
    // Whenever data state is changed either through props or internally through some actions
    // This useEffect will be called
    getSummary(data);
  }, [props?.data]);

  useEffect(() => {
    EventEmitter.subscribe('aiError', handleAIError);
    return function cleanup() {
      EventEmitter.unsubscribe('aiError', handleAIError);
      if (ratingTimeOutRef.current) {
        clearTimeout(ratingTimeOutRef.current);
      }
    };
  }, []);

  const summaryWrapperClass = [
    'summary-type-wrapper',
    apiLoading || (summaryTypeLoading && 'cursor-not-allowed')
  ]
    .filter(Boolean)
    .join(' ');

  const onCopy = () => {
    if (data?.onCopy) {
      data?.onCopy(summaryData, data);
    } else {
      sendActivity({ type: AI_FEEDBACK_TYPES.COPY_PASTE });
    }
  };

  const DataComponent = data?.dataComponent;

  // const isBackButtonHidden = data?.hideBack || isEmpty(options);
  const isBackButtonHidden = data?.hideBack
    ? data?.hideBack && !isEmpty(summaryData)
    : isEmpty(options);

  return (
    <div
      id="ai-data-container-wrapper"
      className={`position-relative ${isBeta ? 'pt10' : ''}`}
    >
      {isBeta && (
        <div className="ai-beta-badge position-absolute">
          <Badge status="warningSecondary">
            <div className="text-medium">
              <FormattedMessage id="changeLog.nav.beta.header" />
            </div>
          </Badge>
        </div>
      )}
      {errors ? (
        <div>
          <div className="flex g10 actr">
            <div className="ai-container-bot flex">
              <GlobalAIBot riveState={GLOBAL_AI_BOT_STATE.entry} />
            </div>
            <div className="text-white">
              <TextStyle>{errors?.message}</TextStyle>
            </div>
          </div>

          {!isBackButtonHidden && (
            <div className="mt-10">
              <Button plain onClick={onBack} aiPlain>
                <FormattedMessage id="ticket.newProject.cancel.goback" />
              </Button>
            </div>
          )}
        </div>
      ) : (
        <div className="ai-summary-container-new">
          <div className="flex actr g10 mb10">
            <div className="ai-container-bot">
              <GlobalAIBot riveState={GLOBAL_AI_BOT_STATE.entry} />
            </div>

            <div>
              <TextStyle>
                <div className="text-white">
                  <RandomText text={headerText} maintainText />
                </div>
              </TextStyle>
            </div>
          </div>

          {showRatingComponent && (
            <AIOnboardingRatingComponent
              featureName={aiData?.featureName}
              // onCompleted={closeRatingComponent}
            />
          )}

          {summaryTypes && AI_TYPE === 'SUMMARY' && (
            <div className="flex g10 actr mb12">
              {summaryTypes?.map((type) => {
                const summaryTypeClass = [
                  'ai-summary-type p5 pl10 pr10 br20 pointer white-color fs10',
                  type === summaryType && 'active',
                  apiLoading && 'pointer-events-none'
                ]
                  .filter(Boolean)
                  .join(' ');

                return (
                  <div className={summaryWrapperClass}>
                    <div
                      className={summaryTypeClass}
                      onClick={() => changeSummaryType(type)}
                    >
                      {type}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          {/* {!isEmpty(prompts) &&
            !isEmpty(currentPrompt) &&
            AI_TYPE === 'REPHRASE' && (
              <AIRephraseHeader
                prompts={prompts}
                disabled={apiLoading}
                currentPrompt={currentPrompt}
                onChange={onRephraseChange}
              />
            )} */}
          {(summaryData || data?.skipAPI) && (
            <div className={!data?.plainBackground && 'p10 br10 ai-dark-bg'}>
              {DataComponent ? (
                <DataComponent content={summaryData} data={data} />
              ) : (
                <>
                  {(data?.headingIcon ||
                    data?.headingText ||
                    !data?.hideCopyPaste) && (
                    <div className="mb-10 flex fx-sb actr">
                      <div className="flex g10">
                        <div className="flex">
                          <SVGIcon name={data?.headingIcon} />
                        </div>

                        <div className="text-white">
                          <TextStyle weight="medium">
                            {data?.headingText}
                          </TextStyle>
                        </div>
                      </div>

                      {!data?.hideCopyPaste && (
                        <AICopyAndPaste
                          data={data}
                          summaryData={summaryData}
                          sendActivity={sendActivity}
                          showRatingOnCopy={data?.showRatingOnCopy}
                          setShowRating={() => setShowRatingComponent(true)}
                        />
                      )}
                    </div>
                  )}

                  <div
                    className="ai-summary-content ai-html-content scroll ws-prewrap white-color ai-content-container-max-height"
                    dangerouslySetInnerHTML={{
                      __html: dompurifier(summaryData)
                    }}
                  ></div>
                </>
              )}
            </div>
          )}

          {!apiLoading && (!data?.hideFeedback || !data?.hideBack) && (
            <div
              className={`fx actr summary-actions mt-10 ${
                isBackButtonHidden ? 'jend' : 'space-between'
              }`}
            >
              {!isBackButtonHidden && (
                <div>
                  <Button plain onClick={onBack} aiPlain>
                    <FormattedMessage id="ticket.newProject.cancel.goback" />
                  </Button>
                </div>
              )}

              <div>
                {!data?.hideFeedback && (
                  <AIFeedback
                    isHideFeedback={hideFeedback}
                    hideCustomFeedback={AI_TYPE === 'REPHRASE'}
                    sendActivity={sendActivity}
                  />
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AIDataComponent;
